import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ShoppingCart,
  X,
  Trash2,
  PlusCircle,
  MinusCircle,
  Menu,
  ChevronDown,
  Phone,
  Upload,
  Zap,
  MapPin,
  Search,
  Percent,
  User,
  Package,
} from "react-feather";
import { useCart } from "./Cart";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { filterHiddenCategories, getProductImage } from "./utils/categoryUtils";
import UploadModal from './components/UploadModal';
import DoctorModal from './components/DoctorModal';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const {
    cartItems,
    addToCart,
    removeFromCart,
    clearCart,
    updateCartItemQuantity,
  } = useCart();
  const cartRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [iconBounce, setIconBounce] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Add null checks to prevent errors
      if (!event || !event.target) return;

      try {
        // Ensure clicks inside the dropdown or on scrollbars don't close it
        if (
          cartRef.current &&
          !cartRef.current.contains(event.target) &&
          (!searchRef.current || !searchRef.current.contains(event.target))
        ) {
          setCartOpen(false);
        }
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setIsOpen(false);
        }
        // Close search results only if the click is outside both the search input and dropdown
        if (searchRef.current && !searchRef.current.contains(event.target)) {
          setFilteredProducts([]);
        }
      } catch (error) {
        console.error("Error in click outside handler:", error);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (cartItems.length > 0) {
      setIconBounce(true);
      const timeout = setTimeout(() => setIconBounce(false), 500);
      return () => clearTimeout(timeout);
    }
  }, [cartItems]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          "https://dawamaxma.dawamtaani.co.ke/api/pharmacy"
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        // Filter out hidden categories
        const filteredData = Array.isArray(data)
          ? filterHiddenCategories(data)
          : data;
        setProducts(filteredData);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      // Filter by search term and exclude hidden categories
      const filtered = products.filter((product) =>
        product.itemName?.toLowerCase().includes(value.toLowerCase())
      );
      // Double-check to filter hidden categories (should already be filtered, but ensure)
      const finalFiltered = filterHiddenCategories(filtered);
      setFilteredProducts(finalFiltered);
    } else {
      setFilteredProducts([]);
    }
  };

  const handleSelectProduct = useCallback(
    (productId) => {
      setSearchTerm("");
      setFilteredProducts([]);
      setTimeout(() => {
        navigate(`/product-details/${productId}`);
      }, 100);
    },
    [navigate]
  );

  const handleCheckout = () => {
    if (cartItems.length === 0) return;
    const message = cartItems
      .map(
        (item) => `${item.quantity} x ${item.itemName} at ${item.retailPrice}`
      )
      .join(", ");
    const whatsappUrl = `https://wa.me/254101757541?text=I am interested in: ${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const toggleCart = () => setCartOpen(!cartOpen);

  const handleViewCart = () => {
    toggleCart();
    navigate("/view-cart");
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  const toggleUploadModal = () => setIsUploadModalOpen(!isUploadModalOpen);
  const toggleDoctorModal = () => setIsDoctorModalOpen(!isDoctorModalOpen);

  const navLinks = [
    { name: "Shop ", to: "/shop?category=supplement" },
   // { name: "Shop by Condition", to: "/shop" },
   // { name: "Shop by Brand", to: "/shop" },
    { name: "Blogs", to: "/blogs" },
   // { name: "My Health Center", to: "/shop" },
  ];

  return (
    <>
      <nav className="sticky top-0 bg-white/95 backdrop-blur-md shadow-soft w-full z-50 border-b border-neutral-100">
      {/* Main Navbar */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <NavLink to="/" className="flex items-center group flex-shrink-0">
            <img
              src={process.env.PUBLIC_URL + "/logo512.png"}
              alt="DawaMtaani Logo"
              className="h-16 w-auto cursor-pointer transition-transform duration-300 group-hover:scale-105"
            />
          </NavLink>

          {/* Search Bar (Desktop) */}
          <div className="hidden md:flex flex-grow max-w-xl mx-8 relative" ref={searchRef}>
            <div className="relative w-full">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search for medications, supplements..."
                className="w-full pl-6 pr-12 py-3 bg-neutral-50 border border-neutral-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-neutral-700 placeholder-neutral-400 transition-all duration-300 hover:border-neutral-300"
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-neutral-400" />
              </div>
            </div>
            {/* Search Results Dropdown */}
            {filteredProducts.length > 0 && (
              <motion.div
                className="absolute top-full left-0 right-0 bg-white shadow-large rounded-2xl border border-neutral-100 z-20 max-h-80 overflow-y-auto mt-2"
                onMouseDown={(e) => e.stopPropagation()}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    onClick={() => handleSelectProduct(product.id)}
                    className="cursor-pointer px-6 py-4 hover:bg-primary-50 flex items-center justify-between border-b border-neutral-100 last:border-b-0 transition-colors duration-200"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                      <span className="text-neutral-700 font-medium">
                        {product.itemName}
                      </span>
                    </div>
                    <span className="text-sm text-primary-600 font-semibold">
                      Ksh {product.retailPrice}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>

          {/* Location & Icons (Desktop) */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Location Selector */}
            <div className="flex items-center bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2.5 rounded-full cursor-pointer hover:from-primary-700 hover:to-primary-800 transition-all duration-300 shadow-md group/loc">
              <div className="bg-white/20 p-2 rounded-lg mr-3 group-hover/loc:bg-white/30 transition-colors">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider font-bold text-white/80 leading-none mb-1">Free Delivery For Orders Above </span>
                <div className="flex items-center">
                  <span className="text-sm font-bold">Ksh 2,500</span>
                  {/* <ChevronDown className="w-4 h-4 ml-1 text-white/60" /> */}
                </div>
              </div>
            </div>

            {/* Action Icons */}
            <div className="flex items-center space-x-2">
             
              <motion.div
                className="relative p-3 rounded-full bg-neutral-100 hover:bg-primary-50 transition-colors duration-300 cursor-pointer group"
                onClick={toggleCart}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  animate={iconBounce ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <ShoppingCart className="w-5 h-5 text-neutral-700 group-hover:text-primary-600 transition-colors duration-300" />
                </motion.div>
                {cartItems.length > 0 && (
                  <motion.span
                    className="absolute -top-1 -right-1 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold shadow-medium"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  >
                    {cartItems.length}
                  </motion.span>
                )}
              </motion.div>
            </div>
          </div>

          {/* Mobile Menu & Cart Toggle */}
          <div className="md:hidden flex items-center space-x-3">
            <motion.button
              onClick={toggleMenu}
              className="p-2 rounded-xl bg-neutral-100 hover:bg-neutral-200 transition-colors duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Menu className="w-5 h-5 text-neutral-700" />
            </motion.button>

            <motion.div
              className="relative p-2 rounded-xl bg-neutral-100 hover:bg-neutral-200 transition-colors duration-300 cursor-pointer"
              onClick={toggleCart}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={iconBounce ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.5 }}
              >
                <ShoppingCart className="w-5 h-5 text-neutral-700" />
              </motion.div>
              {cartItems.length > 0 && (
                <motion.span
                  className="absolute -top-1 -right-1 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs font-bold shadow-medium"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  {cartItems.length}
                </motion.span>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Navigation Links & Action Buttons (Desktop Only) */}
      <div className="hidden md:block border-t border-neutral-100">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.to}
                className="flex items-center text-sm font-medium text-neutral-600 hover:text-primary-600 transition-colors duration-200"
              >
                {link.name}
                <ChevronDown className="w-4 h-4 ml-1 opacity-50" />
              </NavLink>
            ))}
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={toggleDoctorModal}
              className="flex items-center space-x-2 bg-neutral-800 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:bg-neutral-900 transition-all duration-300 shadow-soft"
            >
              <span>Speak to a Doctor</span>
            </button>
            <button 
              onClick={toggleUploadModal}
              className="flex items-center space-x-2 bg-primary-500 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:bg-primary-600 transition-all duration-300 shadow-soft"
            >
              <Upload className="w-4 h-4" />
              <span>Upload a Prescription</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar (Mobile) */}
      <div className="md:hidden flex items-center justify-center p-4">
        <div
          className="flex items-center bg-gray-100 rounded-full px-4 py-2 w-full max-w-md relative"
          ref={searchRef}
        >
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search for products"
            className="bg-transparent outline-none text-gray-600 flex-grow"
          />
          <button className="text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M12.9 14.32a8 8 0 111.414-1.414l4.387 4.387a1 1 0 01-1.414 1.414l-4.387-4.387zM8 14A6 6 0 108 2a6 6 0 000 12z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {/* Search Results Dropdown (Mobile) */}
          {filteredProducts.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 bg-white shadow-lg rounded-b-lg z-10 max-h-60 overflow-y-auto"
              onMouseDown={(e) => e.stopPropagation()} // Prevent dropdown from closing on clicks within
            >
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleSelectProduct(product.id)}
                  className="cursor-pointer px-4 py-2 hover:bg-gray-200 flex items-center justify-between"
                >
                  <span className="text-gray-700">{product.itemName}</span>
                  <span className="text-sm text-gray-500">
                    Ksh {product.retailPrice}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>



      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            className="fixed inset-0 bg-white text-gray-600 p-4 z-20 overflow-y-auto"
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Close Button */}
            <div className="flex justify-end">
              <X
                className="text-gray-800 cursor-pointer"
                onClick={toggleMenu}
              />
            </div>
            {/* <NavLink
              to="/shop"
              className="block py-2 hover:text-gray-800"
              onClick={toggleMenu}
            >
              Shop
            </NavLink> */}
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.to}
                className="block py-2 hover:text-gray-800"
                onClick={toggleMenu}
              >
                {link.name}
              </NavLink>
            ))}
            {/* <NavLink
              to="/blogs"
              className="block py-2 hover:text-gray-800"
              onClick={toggleMenu}
            >
              Blogs
            </NavLink> */}

            {/* Mobile Action Buttons */}
            <div className="mt-8 space-y-4">
              <button
                onClick={() => {
                  toggleDoctorModal();
                  toggleMenu();
                }}
                className="w-full flex items-center justify-center space-x-2 bg-neutral-800 text-white px-5 py-3 rounded-full text-sm font-bold hover:bg-neutral-900 transition-all duration-300 shadow-soft"
              >
                <span>Speak to a Doctor</span>
              </button>
              <button
                onClick={() => {
                  toggleUploadModal();
                  toggleMenu();
                }}
                className="w-full flex items-center justify-center space-x-2 bg-primary-500 text-white px-5 py-3 rounded-full text-sm font-bold hover:bg-primary-600 transition-all duration-300 shadow-soft"
              >
                <Upload className="w-4 h-4" />
                <span>Upload a Prescription</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Drawer */}
      <AnimatePresence>
        {cartOpen && (
          <motion.div
            ref={cartRef}
            className="fixed top-20 right-4 w-[95%] sm:w-[700px] bg-white shadow-2xl z-50 rounded-lg border border-neutral-200 overflow-hidden"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Popover Arrow */}
            <div className="absolute top-0 right-10 w-4 h-4 bg-white border-l border-t border-neutral-200 transform -translate-y-1/2 rotate-45 z-10" />
            <div className="relative flex flex-col bg-white">
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto max-h-[70vh]">
                {cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 px-6">
                    <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
                      <ShoppingCart className="w-10 h-10 text-neutral-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-700 mb-2">
                      Your cart is empty
                    </h3>
                    <p className="text-neutral-500 text-center text-sm">
                      Add some products to get started!
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-100">
                    {cartItems.map((item, index) => (
                      <div
                        key={item.id || index}
                        className="p-4 transition-all duration-300 relative group"
                      >
                        <div className="flex items-start sm:items-center space-x-4">
                          <div className="relative flex-shrink-0">
                            <img
                              src={getProductImage(
                                item,
                                "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?q=80&w=300"
                              )}
                              alt={item.itemName}
                              className="w-16 h-16 sm:w-14 sm:h-14 object-contain"
                              onError={(e) => {
                                console.error("Image failed to load:", e.target.src);
                                e.target.src = "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?q=80&w=300";
                              }}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-neutral-800 text-[14px] sm:text-[15px] leading-tight line-clamp-2">
                              {item.itemName}
                            </h4>
                            <p className="text-[12px] sm:text-[13px] text-neutral-400 mt-1">
                              By {item.itemName}
                            </p>
                            <div className="sm:hidden mt-2">
                              <p className="font-semibold text-neutral-700 text-[14px]">
                                KES{" "}
                                {item.retailPrice
                                  ? (
                                      parseFloat(item.retailPrice) * item.quantity
                                    ).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                  : "0.00"}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                            <div className="flex items-center bg-white rounded-full px-1.5 py-1 border border-neutral-100">
                              <button
                                onClick={() => {
                                  try {
                                    updateCartItemQuantity(
                                      item.id,
                                      item.quantity - 1
                                    );
                                  } catch (error) {
                                    console.error(
                                      "Error updating cart quantity:",
                                      error
                                    );
                                  }
                                }}
                                className="w-7 h-7 flex items-center justify-center text-neutral-400 hover:text-primary-600 transition-colors bg-neutral-50 rounded-full"
                              >
                                <span className="text-lg font-normal">-</span>
                              </button>
                              <span className="text-sm font-semibold text-neutral-700 mx-3 min-w-[15px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => {
                                  try {
                                    updateCartItemQuantity(
                                      item.id,
                                      item.quantity + 1
                                    );
                                  } catch (error) {
                                    console.error(
                                      "Error updating cart quantity:",
                                      error
                                    );
                                  }
                                }}
                                className="w-7 h-7 flex items-center justify-center text-neutral-400 hover:text-primary-600 transition-colors bg-neutral-50 rounded-full"
                              >
                                <span className="text-lg font-normal">+</span>
                              </button>
                            </div>

                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-2 sm:p-3 rounded-full sm:rounded-[20px] border border-neutral-200 text-neutral-400 hover:text-red-500 hover:bg-red-50 hover:border-red-100 transition-all"
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>

                          <div className="hidden sm:block text-right flex-shrink-0 min-w-[100px]">
                            <p className="font-medium text-neutral-700 text-[15px]">
                              KES{" "}
                              {item.retailPrice
                                ? (
                                    parseFloat(item.retailPrice) * item.quantity
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : "0.00"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {cartItems.length > 0 && (
                <div className="bg-white">
                  <div className="flex justify-between items-center bg-[#F2F3F5] px-6 py-4 mx-2 rounded-lg">
                    <span className="text-lg text-neutral-600">
                      Total
                    </span>
                    <span className="text-lg font-semibold text-neutral-800">
                      KES{" "}
                      {cartItems
                        .reduce(
                          (total, item) =>
                            total +
                            parseFloat(item.retailPrice || 0) * item.quantity,
                          0
                        )
                        .toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </span>
                  </div>

                  <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <motion.button
                      onClick={clearCart}
                      className="w-full sm:w-auto bg-[#D81B60] text-white px-8 py-3 rounded-full text-[15px] font-semibold hover:bg-[#C2185B] transition-all shadow-md"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Clear all
                    </motion.button>

                    <div className="flex flex-row items-center gap-3 w-full sm:w-auto">
                      <motion.button
                        onClick={handleViewCart}
                        className="flex-1 sm:flex-none bg-white text-neutral-700 border border-neutral-300 px-6 py-3 rounded-full text-[15px] font-semibold hover:bg-neutral-50 transition-all text-center shadow-sm"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        View Cart
                      </motion.button>

                      <motion.button
                        onClick={handleCheckout}
                        className="flex-1 sm:flex-none bg-[#7CB342] text-white px-8 py-3 rounded-full text-[15px] font-bold hover:bg-[#689F38] transition-all text-center shadow-lg"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Checkout
                      </motion.button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    {/* Modal Components */}
    <AnimatePresence>
      {isUploadModalOpen && (
        <UploadModal key="upload-modal" closeModal={toggleUploadModal} />
      )}
      {isDoctorModalOpen && (
        <DoctorModal key="doctor-modal" closeModal={toggleDoctorModal} />
      )}
    </AnimatePresence>
  </>
);
};

export default Navbar;
